import * as React from 'react';

// PACKAGES
import { Alert } from 'reactstrap';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import Button from '../../components/Button';

// ICONS
import { ReactComponent as NoteIcon } from '../../assets/svg/note-icon.svg';

// TYPES
import { ComplaintsFormDataTypes } from './ComplaintsSubmit';
import { ComplaintMemberCandidatesUserProfile } from '../../api/models';

export interface StepTwoTypes {
	complaintsFormData: ComplaintsFormDataTypes;
	setComplaintsFormData: React.Dispatch<React.SetStateAction<ComplaintsFormDataTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
	minimumLeaders: number;
	leaders: ComplaintMemberCandidatesUserProfile[];
	familyMembersData: ComplaintMemberCandidatesUserProfile[];
}

const StepTwo: React.FC<StepTwoTypes> = ({
	complaintsFormData,
	setComplaintsFormData,
	setActiveStep,
	minimumLeaders,
	leaders,
	familyMembersData
}) => {
	// Error State
	const [showError, setShowError] = React.useState(false);

	// Filter all leaders Id
	const leaderIds = leaders.map((item) => item.id || '');

	// Set up Data state for Excluded Leadership Team Members and Selected Family Members
	const [excludedLeadershipTeamMembers, setExcludedLeadershipTeamMembers] = React.useState<string[] | undefined>([]);

	const [selectedFamilyMembers, setSelectedFamilyMembers] = React.useState<string[] | undefined>([]);

	React.useEffect(() => {
		setExcludedLeadershipTeamMembers(complaintsFormData.excludedLeaders);

		setSelectedFamilyMembers(complaintsFormData.familyMembers);
	}, [complaintsFormData.excludedLeaders, complaintsFormData.familyMembers, complaintsFormData.assignees]);

	// Show or Hide error when at least one member is added to Selected Leadership team members list
	React.useEffect(() => {
		if (excludedLeadershipTeamMembers && excludedLeadershipTeamMembers.length < leaderIds.length) {
			setShowError(false);
		}
	}, [excludedLeadershipTeamMembers]);

	// Leadership Team members Filter Logic
	const leadershipMemberAddHandler = (memberId: string | undefined) => {
		if (memberId !== undefined) {
			const newExcludedLeadershipMembers = complaintsFormData.excludedLeaders?.filter((item) => item !== memberId);

			setComplaintsFormData({
				...complaintsFormData,

				assignees: [...(complaintsFormData.assignees || []), memberId],
				excludedLeaders: [...(newExcludedLeadershipMembers || [])]
			});

			if (complaintsFormData.assignees && complaintsFormData.assignees.length > 1) {
				setShowError(false);
			}
		}
	};

	const leadershipMemberRemoveHandler = (memberId: string | undefined) => {
		if (memberId !== undefined) {
			const newLeadershipMembers = complaintsFormData.assignees?.filter((item) => item !== memberId);

			setComplaintsFormData({
				...complaintsFormData,

				assignees: [...(newLeadershipMembers || [])],
				excludedLeaders: [...(complaintsFormData.excludedLeaders || []), memberId]
			});
		}
	};

	// Family members Filter Logic
	const familyMemberAddHandler = (memberId: string | undefined) => {
		if (memberId !== undefined) {
			if (complaintsFormData.familyMembers?.filter((item) => item === memberId).length) return;

			setComplaintsFormData({
				...complaintsFormData,

				familyMembers: [...(complaintsFormData.familyMembers || []), memberId]
			});
		}
	};

	const familyMemberRemoveHandler = (memberId: string | undefined) => {
		if (memberId !== undefined) {
			const newFamilyMembers = complaintsFormData.familyMembers?.filter((item) => item !== memberId);

			setComplaintsFormData({
				...complaintsFormData,

				familyMembers: [...(newFamilyMembers || [])]
			});
		}
	};

	// Step two Click Handler
	const stepTwoClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();

		if (excludedLeadershipTeamMembers && excludedLeadershipTeamMembers.length >= leaderIds.length) {
			setShowError(true);
		} else {
			setShowError(false);

			setActiveStep('step3');

			window.scrollTo(0, 0);
		}
	};

	return (
		<div>
			{leaders && leaders.length < (minimumLeaders || 2) ? (
				<Alert color="info">
					<div className="alert-icon">
						<NoteIcon />
					</div>

					<div className="alert-content">
						<p>
							Please note: This organisation has not assigned the minimum number of leaders to resolve complaints.
							Please be aware that your complaint may be shared with other staff members not listed here in order to
							resolve the issue.
						</p>
					</div>
				</Alert>
			) : null}

			{leaders &&
			leaders.length >= (minimumLeaders || 2) &&
			excludedLeadershipTeamMembers &&
			excludedLeadershipTeamMembers.length > 0 &&
			leaderIds.length - excludedLeadershipTeamMembers.length < (minimumLeaders || 2) ? (
				<Alert color="info">
					<div className="alert-icon">
						<NoteIcon />
					</div>

					<div className="alert-content">
						<p>
							Please note: You have included fewer leaders then the maximum number required to resolve complaints. If
							you do not wish to add more leaders to the issue, please be aware that your complaint may be shared with
							other staff members not listed here in order to resolve the issue.
						</p>
					</div>
				</Alert>
			) : null}

			<h2 className="general-header">Who should be notified about this complaint? </h2>

			<p className="sub-grey-text">
				The leadership team members listed below will be notified of this complaint. Only remove a leader from being
				notified in exceptional circumstances (eg – if the complaint is about a leader and you do not want them to
				receive the complaint).
			</p>

			{leaders && leaders.length > 0 ? (
				<section className="info-section">
					<div className="section-header">
						<h2 className="header-name color-dark">Leadership Team members </h2>
					</div>

					<ul>
						{leaders.map((item) => {
							return (
								<li key={item.id} className="row-container">
									<div className="row detail-row">
										<div className="col-2 col-lg-1 pdl-0">
											<CircleImage fullName={item.name} src={item.profileImage} />
										</div>

										<div className="col-6 col-lg-7">
											<div className="row detail-info h-100">
												<div className="col-lg-4 detail-name align-self-center">{item.name}</div>

												<div className="col-lg-8 detail-status align-self-center">
													<span className="label">{item.isOrganisationAdmin ? 'Leader' : item.role}</span>
												</div>
											</div>
										</div>

										<div className="col-4 link-group">
											{excludedLeadershipTeamMembers?.includes(item.id || '') ? (
												<Button className="btn-text" onClick={() => leadershipMemberAddHandler(item.id)}>
													Add
												</Button>
											) : (
												<Button className="btn-text remove" onClick={() => leadershipMemberRemoveHandler(item.id)}>
													Remove
												</Button>
											)}
										</div>
									</div>
								</li>
							);
						})}
					</ul>
				</section>
			) : null}

			{familyMembersData && familyMembersData.length > 0 ? (
				<section className="info-section">
					<div className="section-header">
						<h2 className="header-name color-dark">Family members</h2>
					</div>

					<ul>
						{familyMembersData.map((item) => {
							return (
								<li key={item.id} className="row-container">
									<div className="row detail-row">
										<div className="col-2 col-lg-1 pdl-0">
											<CircleImage fullName={item.name} src={item.profileImage} />
										</div>

										<div className="col-6 col-lg-7">
											<div className="row detail-info h-100">
												<div className="col-lg-4 detail-name align-self-center">{item.name}</div>

												<div className="col-lg-8 detail-status align-self-center">
													<span className="label">{item.isOrganisationAdmin ? 'Leader' : item.role}</span>
												</div>
											</div>
										</div>

										<div className="col-4 link-group">
											{selectedFamilyMembers?.includes(item.id || '') ? (
												<Button className="btn-text remove" onClick={() => familyMemberRemoveHandler(item.id)}>
													Remove
												</Button>
											) : (
												<Button className="btn-text" onClick={() => familyMemberAddHandler(item.id)}>
													Add
												</Button>
											)}
										</div>
									</div>
								</li>
							);
						})}
					</ul>
				</section>
			) : null}

			{showError && (
				<div className="error-message mtop-20">You need to add at least 1 Leadership Team member or Leader</div>
			)}

			<div className="form-button-group">
				<Button type="submit" className="btn-primary submit-btn complaints-submit-page" onClick={stepTwoClickHandler}>
					Continue
				</Button>
			</div>
		</div>
	);
};

export default StepTwo;
